import type { AnalyticsRequest, AnalyticsResponse, AnalyticsAPIResponse } from '@/components/reports/graphs/types';

const ANALYTICS_API_URL = 'https://api.pricingsaas.com/functions/v1/api/analytics';
const ANALYTICS_API_KEY = import.meta.env.VITE_ANALYTICS_API_KEY;

export async function fetchAnalytics(
  request: AnalyticsRequest
): Promise<AnalyticsResponse> {
  // Check if API key is configured
  if (!ANALYTICS_API_KEY) {
    throw new Error('Analytics API key not configured. Please add VITE_ANALYTICS_API_KEY to your .env file.');
  }

  try {
    const requestBody: any = {
      metric: request.metric,
      periods: request.periods,
      options: request.options || {}
    };

    // Include filters if they exist
    if (request.filters && Object.keys(request.filters).length > 0) {
      requestBody.filters = request.filters;
    }

    const response = await fetch(ANALYTICS_API_URL, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${ANALYTICS_API_KEY}`,
      },
      body: JSON.stringify(requestBody)
    });

    if (!response.ok) {
      // Try to parse error response
      let errorMessage = 'Failed to fetch analytics';
      try {
        const errorData = await response.json();
        errorMessage = errorData.message || errorData.error || errorMessage;
      } catch {
        // If parsing fails, use status text
        errorMessage = `${errorMessage}: ${response.statusText}`;
      }

      // Handle specific status codes
      if (response.status === 401 || response.status === 403) {
        throw new Error('Authentication failed. Please sign in again.');
      } else if (response.status === 429) {
        throw new Error('Rate limit exceeded. Please try again later.');
      } else if (response.status >= 500) {
        throw new Error('Analytics service is temporarily unavailable. Please try again later.');
      }

      throw new Error(errorMessage);
    }

    const rawData = await response.json() as AnalyticsAPIResponse;

    // Transform the API response to match our expected format
    const transformedData: AnalyticsResponse = {
      metric: rawData.metric,
      periods: Object.entries(rawData.overall).map(([period, stats]: [string, {
        total: number;
        avg: number;
        count: number;
        median: number;
        min: number;
        max: number;
      }]) => ({
        period,
        total: stats.total,
        average: stats.avg,  // Map 'avg' to 'average'
        median: stats.median,
        min: stats.min,
        max: stats.max,
        count: stats.count,
        breakdown: {
          by_category: rawData.by_category,
          by_employees: rawData.by_employees,
        }
      }))
    };

    return transformedData;
  } catch (error) {
    // Re-throw our custom errors
    if (error instanceof Error) {
      throw error;
    }

    // Handle network errors
    throw new Error('Failed to connect to analytics service. Please check your internet connection.');
  }
}
